// Recovery for version skew. Deploying replaces the whole asset set, so the
// hashed chunks a already-open tab was built against stop existing: the moment
// it navigates to a split route, the import 404s and TanStack Router unmounts
// the tree, leaving a white screen.
//
// Vite's preload helper dispatches `vite:preloadError` before rethrowing, and
// the router's route chunks go through that helper — the generated route tree
// calls it as `__vitePreload(() => import('./blog._blogSlug-<hash>.js'), ...)`,
// and it fires on both the preload <link> failing and the import() itself
// rejecting. So this one listener covers every split route.
//
// Reloading fetches fresh server-rendered HTML pointing at the current hashes,
// which is what makes the recovery stick; src/server.ts sends the HTML
// `Cache-Control: no-cache` so the reload cannot be answered from cache with
// the same stale document.
const RELOAD_MARKER = 't2e:staleChunkReload'

// A reload only fixes a chunk that 404s because it is *outdated*. If the chunk
// is missing from the live build too, reloading finds it missing again and the
// page reload-loops. One attempt per window is enough to tell the two apart:
// past that, let the error through so it surfaces instead of spinning.
const RETRY_WINDOW_MS = 30_000

const recentlyReloaded = (now: number): boolean => {
   try {
      const last = window.sessionStorage.getItem(RELOAD_MARKER)
      return last !== null && now - Number(last) < RETRY_WINDOW_MS
   } catch {
      // Safari in private mode throws on sessionStorage. Without the marker we
      // cannot detect a loop, so treat it as already-reloaded and let the error
      // propagate rather than risk one.
      return true
   }
}

export const installStaleChunkReload = (): void => {
   if (typeof window === 'undefined') return

   window.addEventListener('vite:preloadError', (event) => {
      const now = Date.now()
      if (recentlyReloaded(now)) return

      try {
         window.sessionStorage.setItem(RELOAD_MARKER, String(now))
      } catch {
         return
      }

      // Suppressing the rethrow keeps React from tearing the tree down in the
      // moment before the reload lands. Only done on the path that reloads —
      // otherwise the failed import would resolve to undefined and crash
      // somewhere less legible than the original error.
      event.preventDefault()
      window.location.reload()
   })
}
